import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Divider, Button, TextField,
    CircularProgress, Card, CardContent, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Avatar, Chip,
    Snackbar, Alert, Tabs, Tab, Menu, IconButton, ListSubheader
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
    Person, SupportAgent, SmartToy, LocationOn, AttachMoney, DriveEta, AssignmentInd, Schedule, Phone, Bolt
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import {
    getSupportTicket, getTicketMessages, sendTicketMessage, updateSupportTicket,
    claimSupportTicket, assignSupportTicket, listSupportAgents,
    getTicketEvents, getTicketNotes, addTicketNote, listCannedReplies, getSupportTaxonomy, SupportCategory, revealRequesterPhone,
    getTicketCanned, listMacros, applyMacro,
} from '../services/supportService';
import { socketService } from '../services/socketService';
import { getAdminUidFromToken } from '../utils/auth';
import { SupportTicket, ChatMessage, TicketEvent, TicketNote, CannedReply } from '../types/Support';
import { isOpenStatus, PRIORITY_LABELS, PRIORITY_COLOR, STATUS_LABELS, RESOLUTION_CODES, RESOLUTION_LABELS, QUEUE_LABELS, CSAT_REASON_LABELS } from '../constants/support';
import PageHelp from '../components/common/PageHelp';
import { useCan } from '../auth/Can';
import MvagPanel from '../components/support/MvagPanel';
import TransferDialog from '../components/support/TransferDialog';
import EscalateDialog, { ESCALATION_REASON_LABELS } from '../components/support/EscalateDialog';
import { senderKind, senderLabel, SenderKind } from '../components/support/chatSender';
import Customer360Card from '../components/support/Customer360Card';
import ApprovalsPanel from '../components/support/ApprovalsPanel';
import QaReviewPanel from '../components/support/QaReviewPanel';

// ---- Timeline helpers (module-level: pure, no component state) ----------------------------------

type EventChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
const EVENT_CHIP: Record<string, { label: string; color: EventChipColor }> = {
    created: { label: 'Created', color: 'info' },
    assigned: { label: 'Assigned', color: 'primary' },
    sla_breach: { label: 'SLA', color: 'error' },
    csat: { label: 'CSAT', color: 'success' },
    reopened: { label: 'Reopened', color: 'warning' },
};
const eventChip = (type: string) =>
    EVENT_CHIP[type] ?? { label: type.replace(/[_-]/g, ' '), color: 'default' as EventChipColor };

/** Human one-liner per audit event, e.g. 'Assigned to #7', 'CSAT ★4', 'Reopened (count 2)'. */
const eventSummary = (ev: TicketEvent): string => {
    const p: Record<string, any> = ev.payload || {};
    switch (ev.type) {
        case 'created':
            return `Created (${p.issueType ?? 'ticket'}/${p.priority ?? 'normal'})`;
        case 'assigned': {
            const to = p.assigneeId ?? p.to;
            return to == null ? 'Unassigned (released to queue)' : `Assigned to #${to}`;
        }
        case 'sla_breach':
            return `SLA breached — ${p.level === 'first_response' ? 'first response' : 'resolution'}`;
        case 'csat': {
            const comment = p.comment ? ` — “${p.comment}”` : '';
            const why = p.reason ? ` (${CSAT_REASON_LABELS[p.reason] ?? p.reason})` : '';
            return `CSAT ★${p.score ?? p.csatScore ?? '?'}${why}${comment}`;
        }
        case 'reopened':
            return `Reopened (count ${p.reopenCount ?? p.count ?? '?'})`;
        default:
            return ev.type.replace(/[_-]/g, ' ');
    }
};

const relTime = (iso: string): string => {
    try {
        return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch {
        return iso;
    }
};

// Round 2 F7: bubble look per sender.
const BUBBLE: Record<Exclude<SenderKind, 'system'>, { bg: string; fg: string; align: string; radius: string; avatar: string }> = {
    me: { bg: '#1976d2', fg: '#fff', align: 'flex-end', radius: '20px 20px 0 20px', avatar: '#1976d2' },
    staff: { bg: '#e3f2fd', fg: '#0d2b45', align: 'flex-end', radius: '20px 20px 0 20px', avatar: '#5c6bc0' },
    customer: { bg: '#ffffff', fg: '#000', align: 'flex-start', radius: '20px 20px 20px 0', avatar: '#9c27b0' },
    bot: { bg: '#eeeeee', fg: '#000', align: 'center', radius: '20px', avatar: '#757575' },
};

const byCreatedAtAsc = <T extends { createdAt: string }>(arr: T[]): T[] =>
    arr.slice().sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

const SupportTicketDetail: React.FC = () => {
    const { id } = useParams();
    // F9: read-only staff (support:read only) see the ticket but no actions; the backend enforces it too.
    const { can } = useCan();
    const canWrite = can('support:write');
    const navigate = useNavigate();
    const [ticket, setTicket] = useState<SupportTicket | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [msgError, setMsgError] = useState(false); // review R1: a failed messages load shows Retry, never a silently empty chat
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);

    // Status Update States
    const [pendingStatus, setPendingStatus] = useState<string>('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    // §5.2: resolving needs a resolution code; on hold needs a reason.
    const [resolutionCode, setResolutionCode] = useState('');
    const [holdReason, setHoldReason] = useState('');
    const [statusError, setStatusError] = useState<string | null>(null);

    // Claim / assign + live escalation banner
    const [claiming, setClaiming] = useState(false);
    // Assign-to-agent picker. The small ACTIVE-staff directory also resolves assignee ids to names.
    const [agents, setAgents] = useState<{ id: number; name: string; role: string }[]>([]);
    const [assignOpen, setAssignOpen] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const [escalateOpen, setEscalateOpen] = useState(false);
    const [assignTarget, setAssignTarget] = useState<string>('');
    useEffect(() => {
        listSupportAgents().then((res) => setAgents(res.data.agents || [])).catch(() => {});
    }, []);
    const [banner, setBanner] = useState<{ text: string; severity: 'success' | 'info' | 'warning' | 'error' } | null>(null);
    // Escalation banners (error/warning) stay 60s and can't be dismissed for the first 10s — a 10s
    // auto-hide was being missed. Success/info confirmations stay quick + immediately closable.
    const bannerCritical = banner?.severity === 'error' || banner?.severity === 'warning';
    const [bannerLocked, setBannerLocked] = useState(false);
    useEffect(() => {
        if (!banner) return;
        const critical = banner.severity === 'error' || banner.severity === 'warning';
        setBannerLocked(critical);
        if (!critical) return;
        const t = setTimeout(() => setBannerLocked(false), 10000);
        return () => clearTimeout(t);
    }, [banner]);

    // Left-column side tabs: Timeline (audit events) | Notes (internal, agent-only).
    const [sideTab, setSideTab] = useState<'timeline' | 'notes'>('timeline');
    const [events, setEvents] = useState<TicketEvent[]>([]);
    const [notes, setNotes] = useState<TicketNote[]>([]);
    const [noteDraft, setNoteDraft] = useState('');
    const [noteSaving, setNoteSaving] = useState(false);

    // Canned replies (⚡ quick-insert menu next to the reply box).
    const [canned, setCanned] = useState<CannedReply[]>([]);
    const [taxonomy, setTaxonomy] = useState<SupportCategory[]>([]);
    const [macros, setMacros] = useState<{ id: string; label: string }[]>([]);
    const [macroAnchor, setMacroAnchor] = useState<HTMLElement | null>(null);
    // Phase 2 §5.7: one tap = reply + status + tags + resolution code (the server fills in the ticket's details).
    const runMacro = async (macroId: string) => {
        setMacroAnchor(null);
        try {
            await applyMacro(ticketId, macroId);
            setBanner({ text: 'Macro applied.', severity: 'success' });
            loadData();
        } catch (e: any) {
            setBanner({ text: e?.response?.data?.message || 'Could not apply the macro', severity: 'error' });
            if (e?.response?.status === 409) loadData(); // BL-6: show who owns it now
        }
    };
    // §5.11: numbers arrive masked; "Show number" asks the server (which logs it) for the full one.
    const [revealedPhone, setRevealedPhone] = useState<string | null>(null);
    const handleRevealPhone = async (id: SupportTicket['id']) => {
        try {
            const r = await revealRequesterPhone(id);
            setRevealedPhone(r.data.phone ?? '');
        } catch (err: any) {
            console.error('Failed to reveal the number', err);
            alert(err?.response?.data?.message || 'Could not show the number'); // R2 D10: never a silent failure
        }
    };
    const [cannedAnchor, setCannedAnchor] = useState<null | HTMLElement>(null);

    // "Customer is typing…" indicator + our own throttled typing emits.
    const [customerTyping, setCustomerTyping] = useState(false);
    const typingHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastTypingEmitRef = useRef(0);

    // Read receipts: customer's read watermark (seq) + per-message delivered ids from live events.
    const [readUpToSeq, setReadUpToSeq] = useState(0);
    const [deliveredIds, setDeliveredIds] = useState<Set<string>>(new Set());

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const ticketId = Number(id);
    // Reply is allowed for ANY non-terminal ticket (the backend allows it) — the old check
    // `status === 'open'` wrongly disabled the reply box the moment an agent moved it to In Progress.
    // Also require a chatRoomId: whatsapp/ivr/email tickets are chatRoom-less, so enabling the reply
    // box for them would let the agent type a reply that silently fails server-side.
    const isChatActive = !!ticket && isOpenStatus(ticket.status) && !!ticket.chatRoomId;

    const currentAdminUid = useMemo(() => getAdminUidFromToken(), []);

    // 1. Initial Data Load (HTTP)
    const loadData = async () => {
        try {
            const [ticketRes, msgRes] = await Promise.all([
                getSupportTicket(ticketId),
                // Round 2 F8: a failed messages call must never blank the ticket (the chat keeps what it had + Retry).
                getTicketMessages(ticketId).catch(() => null),
            ]);
            setTicket(ticketRes.data);
            setPendingStatus(ticketRes.data.status);
            setMsgError(!msgRes);
            if (msgRes) {
                // The API returns Newest First (Desc). We sort Ascending (Oldest First)
                // so they appear top-to-bottom correctly.
                const sortedMessages = msgRes.data.sort((a, b) =>
                    new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
                );
                setMessages(sortedMessages);

                // Read-receipt contract: after messages load, tell the server how far we've read
                // so the customer's app can flip our view to "seen".
                const maxSeq = sortedMessages.reduce(
                    (mx, m) => (typeof m.seq === 'number' && m.seq > mx ? m.seq : mx), 0
                );
                if (ticketRes.data.chatRoomId && maxSeq > 0) {
                    socketService.emit('chat:read', { chatRoomId: ticketRes.data.chatRoomId, upToSeq: maxSeq });
                }
            }

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (ticketId) loadData();
    }, [ticketId]);

    // Audit timeline + internal notes for the side tabs (oldest first, like a timeline reads).
    useEffect(() => {
        if (!ticketId) return;
        getTicketEvents(ticketId).then((r) => setEvents(byCreatedAtAsc(r.data.events || []))).catch(() => {});
        getTicketNotes(ticketId).then((r) => setNotes(byCreatedAtAsc(r.data.notes || []))).catch(() => {});
    }, [ticketId]);

    // Canned replies + taxonomy — fetched once per page visit.
    useEffect(() => {
        // Filled in for this ticket ({name}, {rideId}, {fare}…); the plain list if that fails.
        getTicketCanned(ticketId).then((r) => setCanned(r.data.replies || []))
            .catch(() => listCannedReplies().then((r) => setCanned(r.data.replies || [])).catch(() => {}));
        listMacros().then((r) => setMacros(r.data.macros || [])).catch(() => {});
        getSupportTaxonomy().then((r) => setTaxonomy(r.data.categories || [])).catch(() => {});
    }, []);

    // Taxonomy v2: change the sub-category (category, old type and — for safety — priority follow on the server).
    const handleSubCategory = async (t: SupportTicket, subCategory: string) => {
        try {
            await updateSupportTicket(t.id, { subCategory } as any);
            setTicket({ ...t, subCategory });
            loadData(); // R2 D5: queue, category, P0 priority and MVAG clocks change on the server — show them
        } catch (err: any) {
            console.error('Failed to change the topic', err);
            if (err?.response?.status === 409) { setBanner({ text: err?.response?.data?.message || 'Someone else has this ticket.', severity: 'warning' }); loadData(); }
            else setBanner({ text: err?.response?.data?.message || 'Could not change the topic.', severity: 'error' });
        }
    };
    const subLabel = useMemo(() => {
        for (const c of taxonomy) for (const s of c.subs) if (s.id === ticket?.subCategory) return `${c.label} › ${s.label}`;
        return null;
    }, [taxonomy, ticket?.subCategory]);

    // 2. Socket.IO Integration (Real-time)
    useEffect(() => {
        if (!ticket?.chatRoomId) return;

        // A. Connect & Join; R4-A4: join again after every reconnect (a new server socket starts in no chat room).
        socketService.connect();
        socketService.joinRoom(ticket.chatRoomId);
        const chatRoomIdForRejoin = ticket.chatRoomId;
        const rejoin = () => socketService.joinRoom(chatRoomIdForRejoin);
        socketService.on('connect', rejoin);

        // B. Listen for incoming messages
        const roomId = ticket.chatRoomId;
        socketService.onMessageReceived((newMsg: ChatMessage) => {
            // Prevent duplicates if API load and Socket event overlap
            setMessages((prev) => {
                // If message ID already exists, ignore
                if (prev.some(m => m.id === newMsg.id)) return prev;
                // Append to bottom (Oldest -> Newest flow)
                return [...prev, newMsg];
            });
            // Incoming CUSTOMER message → acknowledge read up to its seq (we're looking at the
            // thread) and drop the typing indicator (the text they were typing just arrived).
            const fromMe = String(newMsg.senderUid) === String(currentAdminUid) || newMsg.senderUid === 'ADMIN_SUPPORT';
            const fromCustomer = !fromMe && !newMsg.isBot && newMsg.senderType !== 'agent' && newMsg.senderType !== 'bot';
            if (fromCustomer) {
                if (typeof newMsg.seq === 'number') {
                    socketService.emit('chat:read', { chatRoomId: roomId, upToSeq: newMsg.seq });
                }
                if (typingHideRef.current) clearTimeout(typingHideRef.current);
                setCustomerTyping(false);
            }
        });

        // C. Cleanup on Unmount
        return () => {
            socketService.off('connect', rejoin);
            if (ticket.chatRoomId) {
                socketService.leaveRoom(ticket.chatRoomId);
            }
            socketService.offMessageReceived();
        };
    }, [ticket?.chatRoomId]);

    // 2b. Live chat presence events for the joined room: typing indicator, read/delivered
    // receipts on our own bubbles, and 'chat:reopened' (customer reopened → refetch so the
    // reply box re-enables once status is open again).
    useEffect(() => {
        if (!ticket?.chatRoomId) return;
        const roomId = ticket.chatRoomId;
        socketService.connect();
        const sameRoom = (p: any) => String(p?.chatRoomId) === String(roomId);

        const onTyping = (p: any) => {
            if (!sameRoom(p)) return;
            if (p?.senderType === 'agent' || p?.senderType === 'bot') return; // only the customer counts
            if (typingHideRef.current) clearTimeout(typingHideRef.current);
            if (p?.isTyping) {
                setCustomerTyping(true);
                // Auto-hide in case the "stopped typing" event never lands.
                typingHideRef.current = setTimeout(() => setCustomerTyping(false), 6000);
            } else {
                setCustomerTyping(false);
            }
        };
        const onRead = (p: any) => {
            if (!sameRoom(p)) return;
            if (String(p?.readerUid) === String(currentAdminUid)) return; // ignore our own receipt echo
            const upTo = Number(p?.upToSeq);
            if (Number.isFinite(upTo)) setReadUpToSeq((prev) => Math.max(prev, upTo));
        };
        const onDelivered = (p: any) => {
            if (!sameRoom(p) || p?.messageId == null) return;
            setDeliveredIds((prev) => {
                const next = new Set(prev);
                next.add(String(p.messageId));
                return next;
            });
        };
        const onChatReopened = (p: any) => {
            if (!sameRoom(p)) return;
            loadData(); // status flips back to open → chat re-enables
        };

        socketService.on('chat:typing', onTyping);
        socketService.on('chat:read', onRead);
        socketService.on('chat:delivered', onDelivered);
        socketService.on('chat:reopened', onChatReopened);
        return () => {
            socketService.off('chat:typing', onTyping);
            socketService.off('chat:read', onRead);
            socketService.off('chat:delivered', onDelivered);
            socketService.off('chat:reopened', onChatReopened);
            if (typingHideRef.current) clearTimeout(typingHideRef.current);
        };
    }, [ticket?.chatRoomId]);

    // 3. Live escalations for THIS ticket. The SLA worker pages 'support:slaBreach' and the safety
    // pre-gate pages 'support:safetyEscalation' to the 'admins' room (admin sockets auto-join it).
    // Filter to this ticket/room so the agent working it sees a breach/safety flag the instant it fires.
    useEffect(() => {
        if (!ticket) return;
        const tid = ticket.id;
        const rid = ticket.chatRoomId;
        socketService.connect();
        const onSla = (p: any) => {
            if (String(p?.ticketId) !== String(tid)) return;
            setBanner({
                text: `SLA breached (${p?.level === 'first_response' ? 'first response' : 'resolution'}) — escalated to ${PRIORITY_LABELS[p?.priority] ?? p?.priority ?? 'higher priority'}.`,
                severity: p?.isSafety ? 'error' : 'warning',
            });
            // Reflect the bumped priority without a full reload.
            setTicket((t) => (t ? { ...t, priority: p?.priority ?? t.priority } : t));
        };
        const onSafety = (p: any) => {
            const mine = p?.ticketId != null ? String(p.ticketId) === String(tid) : !!rid && String(p?.chatRoomId) === String(rid);
            if (!mine) return;
            setBanner({ text: 'SAFETY intent flagged on this conversation — treat as P0.', severity: 'error' });
            setTicket((t) => ({ ...t!, priority: 'p0_safety' }));
        };
        socketService.on('support:slaBreach', onSla);
        socketService.on('support:safetyEscalation', onSafety);
        return () => {
            socketService.off('support:slaBreach', onSla);
            socketService.off('support:safetyEscalation', onSafety);
        };
    }, [ticket?.id, ticket?.chatRoomId]);


    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Throttled (~2.5s) 'typing' emit while the agent types in the reply box. Only for the
    // joined room of an active chat — never for chatRoom-less (whatsapp/ivr/email) tickets.
    const emitTyping = () => {
        if (!ticket?.chatRoomId || !isChatActive) return;
        const now = Date.now();
        if (now - lastTypingEmitRef.current < 2500) return;
        lastTypingEmitRef.current = now;
        socketService.emit('chat:typing', { chatRoomId: ticket.chatRoomId, isTyping: true });
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !isChatActive) return;
        try {
            // 1. Send via API (This pushes to Redis Backend)
            await sendTicketMessage(ticketId, newMessage);

            // 2. Optimistic Update not strictly needed if Socket is fast,
            // but if you want it, ensure it's added to the END of the array.
            setNewMessage('');
            // Typing contract: an explicit "stopped typing" on send.
            if (ticket?.chatRoomId) {
                socketService.emit('chat:typing', { chatRoomId: ticket.chatRoomId, isTyping: false });
                lastTypingEmitRef.current = 0;
            }
            if (ticket && (ticket.assigneeId === null || ticket.assigneeId === undefined)) loadData(); // BL-6: the reply claimed it
        } catch (err: any) {
            console.error("Failed to send", err);
            // BL-6: someone else owns it (409) → say who and reload; the typed text stays.
            if (err?.response?.status === 409) {
                setBanner({ text: err?.response?.data?.message || 'Another agent is handling this ticket.', severity: 'warning' });
                loadData();
            } else {
                setBanner({ text: err?.response?.data?.message || 'Could not send the reply.', severity: 'error' });
            }
        }
    };

    // Internal note — optimistic append (temp row swaps for the server note; rolls back on 4xx).
    const handleAddNote = async () => {
        const body = noteDraft.trim();
        if (!body || noteSaving) return;
        const tempId = `tmp-${Date.now()}`;
        const optimistic: TicketNote = {
            id: tempId,
            ticketId,
            authorId: currentAdminUid,
            authorName: 'You',
            body,
            createdAt: new Date().toISOString(),
        };
        setNotes((prev) => [...prev, optimistic]);
        setNoteDraft('');
        setNoteSaving(true);
        try {
            const res = await addTicketNote(ticketId, body);
            const saved = res.data?.note;
            setNotes((prev) => prev.map((n) => (n.id === tempId && saved ? saved : n)));
        } catch (err) {
            console.error('Failed to add note', err);
            setNotes((prev) => prev.filter((n) => n.id !== tempId));
            setNoteDraft(body); // give the agent their text back
            setBanner({ text: 'Failed to add note.', severity: 'error' });
        } finally {
            setNoteSaving(false);
        }
    };


    const handleStatusSelect = (newStatus: string) => setPendingStatus(newStatus);
    const handleUpdateClick = () => {
        if (ticket && pendingStatus !== ticket.status) { setResolutionCode(''); setHoldReason(''); setStatusError(null); setConfirmOpen(true); }
    };
    const needsCode = pendingStatus === 'resolved';
    const needsReason = pendingStatus === 'on_hold';
    const canConfirm = (!needsCode || !!resolutionCode) && (!needsReason || !!holdReason.trim());
    const handleConfirmUpdate = async (t: SupportTicket) => {
        const patch: any = { status: pendingStatus };
        if (needsCode) patch.resolutionCode = resolutionCode;
        if (needsReason) patch.holdReason = holdReason.trim();
        try {
            await updateSupportTicket(t.id, patch);
            setTicket({ ...t, ...patch });
            setConfirmOpen(false);
            if (t.assigneeId == null) loadData(); // round 2 F4: the change claimed it — show "Assigned to you"
        } catch (err: any) {
            console.error("Failed to update status", err);
            if (err?.response?.status === 409) { // round 2 F4: someone else owns it now
                setConfirmOpen(false);
                setBanner({ text: err?.response?.data?.message || 'Someone else has this ticket.', severity: 'warning' });
                loadData();
                return;
            }
            setStatusError(err?.response?.data?.message || 'Could not change the status');
        }
    };

    // Claim = optimistic lock (first agent wins). 409 → someone beat us; refresh to show who.
    const handleClaim = async () => {
        if (!ticket) return;
        setClaiming(true);
        try {
            await claimSupportTicket(ticket.id);
            setBanner({ text: 'Ticket claimed — it is yours now.', severity: 'success' });
            await loadData();
        } catch (err: any) {
            if (err?.response?.status === 409) {
                setBanner({ text: 'Already claimed by another agent.', severity: 'warning' });
                await loadData();
            } else {
                setBanner({ text: 'Failed to claim ticket.', severity: 'error' });
            }
        } finally {
            setClaiming(false);
        }
    };

    // BL-6: take a colleague's ticket in one tap (their phone is told); then you can reply.
    const handleAssignToMe = async () => {
        if (!ticket || !currentAdminUid) return;
        setClaiming(true);
        try {
            await assignSupportTicket(ticket.id, Number(currentAdminUid));
            setBanner({ text: 'The ticket is yours now.', severity: 'success' });
            await loadData();
        } catch (err: any) {
            setBanner({ text: err?.response?.data?.message || 'Failed to assign the ticket to you.', severity: 'error' });
        } finally {
            setClaiming(false);
        }
    };

    // Release = unassign (assigneeId → null) so it returns to the unclaimed pool.
    const handleRelease = async () => {
        if (!ticket) return;
        setClaiming(true);
        try {
            await assignSupportTicket(ticket.id, null);
            setBanner({ text: 'Ticket released back to the queue.', severity: 'info' });
            await loadData();
        } catch (err) {
            setBanner({ text: 'Failed to release ticket.', severity: 'error' });
        } finally {
            setClaiming(false);
        }
    };

    // Assign/reassign to a specific agent picked from the ACTIVE-staff directory.
    const handleAssign = async () => {
        if (!ticket || !assignTarget) return;
        setClaiming(true);
        try {
            await assignSupportTicket(ticket.id, Number(assignTarget));
            const name = agents.find(a => String(a.id) === assignTarget)?.name || `Agent #${assignTarget}`;
            setBanner({ text: `Ticket assigned to ${name}.`, severity: 'success' });
            setAssignOpen(false);
            await loadData();
        } catch (err) {
            setBanner({ text: 'Failed to assign ticket.', severity: 'error' });
        } finally {
            setClaiming(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    if (!ticket) return <Box sx={{ p: 3 }}><Typography>Ticket not found</Typography></Box>;

    // Helper for visual rows in the context panel
    const ContextRow = ({ icon, label, value }: { icon: React.ReactNode, label: string, value: React.ReactNode }) => (
        <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {icon}
                <Typography variant="subtitle2" color="textSecondary">{label}</Typography>
            </Box>
            <Typography variant="body2" component="div" sx={{ fontWeight: 500 }}>{value || '-'}</Typography>
        </Box>
    );

    // Assignment + SLA derived state
    const isAssigned = ticket.assigneeId !== null && ticket.assigneeId !== undefined;
    const assignedToMe = isAssigned && String(ticket.assigneeId) === String(currentAdminUid);
    const canClaim = !isAssigned && isOpenStatus(ticket.status);
    const assigneeName = isAssigned
        ? (ticket.assigneeName || agents.find(a => String(a.id) === String(ticket.assigneeId))?.name || `Agent #${ticket.assigneeId}`)
        : null;
    // BL-6: one owner per ticket — only the owner replies; others see who has it (and can take it).
    const othersTicket = isAssigned && !assignedToMe;
    // Round 2 F4 (owner D1): status / topic / escalate follow the same rule — yours, or unassigned (the change claims it).
    const canChange = canWrite && !othersTicket;
    const fmtDue = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '-');
    const nowMs = Date.now();
    const firstOverdue = !ticket.firstResponseAt && !!ticket.slaFirstDueAt && new Date(ticket.slaFirstDueAt).getTime() < nowMs && isOpenStatus(ticket.status);
    const resolveOverdue = !ticket.resolvedAt && !!ticket.slaResolveDueAt && new Date(ticket.slaResolveDueAt).getTime() < nowMs && isOpenStatus(ticket.status);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, maxWidth: 1600, mx: 'auto', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, alignSelf: 'flex-start' }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/support-tickets')}>
                Back to Ticket List
              </Button>
              {ticket.publicNumber && (
                <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{ticket.publicNumber}</Typography>
              )}
              {ticket.priority && (
                <Chip size="small" variant="outlined" label={PRIORITY_LABELS[ticket.priority] ?? ticket.priority} color={PRIORITY_COLOR[ticket.priority] ?? 'default'} />
              )}
              {/* Post-resolution signals: how the customer rated us, and whether they pulled it back open. */}
              {ticket.csatScore != null && (
                <Chip size="small" variant="outlined" color={ticket.csatScore <= 2 ? 'error' : 'success'} label={`★ ${ticket.csatScore}/5${ticket.csatReason ? ` — ${CSAT_REASON_LABELS[ticket.csatReason] ?? ticket.csatReason}` : ''}`} />
              )}
              {(ticket.reopenCount ?? 0) > 0 && (
                <Chip size="small" variant="outlined" color="warning" label={`Reopened ×${ticket.reopenCount}`} />
              )}
              <PageHelp k="support" />
            </Box>

            {/* Top area (ticket detail + chat) keeps a full-viewport height so both panels stay tall;
                the related-ride card below adds to page length and is reached by scrolling the page. */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, height: { xs: 'auto', md: 'calc(100vh - 170px)' }, minHeight: 0, overflow: 'hidden' }}>

                {/* LEFT PANEL — the column itself does NOT scroll; each card below owns its own scroll
                    area (ticket card + timeline/notes), so both stay usable independently. */}
                <Box sx={{ flex: { xs: 'none', md: 1 }, minWidth: 0, minHeight: 0, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Ticket card — capped height, scrolls internally. */}
                    <Card variant="outlined" sx={{ borderRadius: 2, maxHeight: { md: '48%' }, display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ overflowY: 'auto' }}>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                Ticket #{ticket.id}
                                <Chip label={STATUS_LABELS[ticket.status] ?? ticket.status} color={ticket.status === 'open' ? 'error' : ticket.status === 'resolved' ? 'success' : 'warning'} size="small" variant="filled" />
                            </Typography>
                            {canWrite && othersTicket && (
                            <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }} data-testid="status-handled-by">
                                <Typography variant="body2" color="textSecondary">Handled by <strong>{assigneeName}</strong> — only the owner changes the status.</Typography>
                                <Button size="small" variant="outlined" disabled={claiming} onClick={handleAssignToMe}>Assign to me</Button>
                            </Box>
                            )}
                            {canChange && (
                            <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Update Status</InputLabel>
                                    <Select value={pendingStatus} label="Update Status" onChange={(e) => handleStatusSelect(e.target.value)}>
                                        {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Button variant="contained" color="primary" disabled={pendingStatus === ticket.status} onClick={handleUpdateClick} sx={{ minWidth: 50, height: 40 }} aria-label={isAssigned ? 'Save status' : 'Claim & save status'}>
                                    <SaveIcon fontSize="small" />
                                </Button>
                            </Box>
                            {!isAssigned && <Typography variant="caption" color="textSecondary" data-testid="claim-on-change">Saving a status or topic, or escalating, claims this ticket for you.</Typography>}
                            </Box>
                            )}
                            <Divider sx={{ my: 2 }} />
                            <ContextRow icon={<SupportAgent fontSize="small" color="action"/>} label="Issue Type" value={<Chip label={ticket.issueType} size="small" />} />
                            {ticket.status === 'on_hold' && ticket.holdReason && (
                                <ContextRow icon={<Schedule fontSize="small" color="action"/>} label="On hold — waiting on" value={ticket.holdReason} />
                            )}
                            {ticket.resolutionCode && (
                                <ContextRow icon={<SupportAgent fontSize="small" color="action"/>} label="Resolution" value={RESOLUTION_LABELS[ticket.resolutionCode] ?? ticket.resolutionCode} />
                            )}
                            {ticket.escalated && (
                                <ContextRow icon={<AssignmentInd fontSize="small" color="action"/>} label="Escalated"
                                    value={<Chip size="small" color="error" label={`${ticket.escalationTier ?? 'L2'}${ticket.escalationReason ? ` · ${ESCALATION_REASON_LABELS[ticket.escalationReason] ?? ticket.escalationReason}` : ''}`} />} />
                            )}
                            {ticket.queue && <ContextRow icon={<AssignmentInd fontSize="small" color="action"/>} label="Queue" value={QUEUE_LABELS[ticket.queue] ?? ticket.queue} />}
                            <ContextRow
                                icon={<SupportAgent fontSize="small" color="action"/>}
                                label="Topic"
                                value={canChange && taxonomy.length ? (
                                    <Select size="small" value={ticket.subCategory ?? ''} displayEmpty onChange={(e) => handleSubCategory(ticket, String(e.target.value))} inputProps={{ 'aria-label': 'Topic' }} sx={{ minWidth: 240 }}>
                                        <MenuItem value="" disabled>Pick a topic</MenuItem>
                                        {taxonomy.flatMap((c) => [
                                            <ListSubheader key={`h-${c.id}`}>{c.label}</ListSubheader>,
                                            ...c.subs.map((s) => <MenuItem key={s.id} value={s.id}>{s.label} · {s.level}</MenuItem>),
                                        ])}
                                    </Select>
                                ) : (subLabel ?? ticket.subCategory ?? '—')}
                            />

                            {/* Who raised it — name + phone so the agent can call back without hunting
                                through user admin. Server enriches from createdByUid; null for bot/agent-created. */}
                            {ticket.requester && (
                                <ContextRow
                                    icon={<Phone fontSize="small" color="action"/>}
                                    label={`Requester (${ticket.requesterType ?? ticket.requester.role})`}
                                    value={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <span>{ticket.requester.name || 'Unnamed'}</span>
                                            {revealedPhone !== null ? (
                                                revealedPhone
                                                    ? <a href={`tel:${revealedPhone}`} style={{ fontFamily: 'monospace', textDecoration: 'none' }}>{revealedPhone}</a>
                                                    : <span>(no number on file)</span>
                                            ) : (
                                                <>
                                                    <span style={{ fontFamily: 'monospace' }}>{ticket.requester.phone}</span>
                                                    {canWrite && <Button size="small" onClick={() => handleRevealPhone(ticket.id)}>Show number</Button>}
                                                </>
                                            )}
                                        </Box>
                                    }
                                />
                            )}

                            <ContextRow icon={<Person fontSize="small" color="action"/>} label="Created By UID" value={ticket.createdByUid.substring(0, 10) + "..."} />

                            {/* Assignment — claim (optimistic lock) or release back to the queue. */}
                            <ContextRow
                                icon={<AssignmentInd fontSize="small" color="action"/>}
                                label="Assignment"
                                value={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        {isAssigned ? (
                                            <Chip
                                                size="small"
                                                color={assignedToMe ? 'primary' : 'default'}
                                                label={assignedToMe ? 'Assigned to you' : assigneeName}
                                            />
                                        ) : (
                                            <Chip size="small" variant="outlined" label="Unassigned" />
                                        )}
                                        {canWrite && canClaim && (
                                            <Button size="small" variant="contained" disabled={claiming} onClick={handleClaim}>
                                                Claim
                                            </Button>
                                        )}
                                        {/* review R1: also on a colleague's resolved ticket — only the owner can reopen it */}
                                        {canWrite && othersTicket && (
                                            <Button size="small" variant="contained" disabled={claiming} onClick={handleAssignToMe} data-testid="btn-assign-me">
                                                Assign to me
                                            </Button>
                                        )}
                                        {/* Assign/reassign to any ACTIVE agent — the manager flow. */}
                                        {canWrite && isOpenStatus(ticket.status) && (
                                            <Button size="small" variant="outlined" disabled={claiming} onClick={() => { setAssignTarget(''); setAssignOpen(true); }}>
                                                Assign…
                                            </Button>
                                        )}
                                        {canWrite && isOpenStatus(ticket.status) && (
                                            <Button size="small" variant="text" disabled={claiming} onClick={() => setTransferOpen(true)}>
                                                Transfer…
                                            </Button>
                                        )}
                                        {canChange && isOpenStatus(ticket.status) && ticket.escalationTier !== 'admin' && (
                                            <Button size="small" variant="text" color="error" onClick={() => setEscalateOpen(true)}>
                                                Escalate…
                                            </Button>
                                        )}
                                        {canWrite && isAssigned && (
                                            <Button size="small" variant="text" color="inherit" disabled={claiming} onClick={handleRelease}>
                                                Release
                                            </Button>
                                        )}
                                    </Box>
                                }
                            />

                            {/* SLA clocks — red when the window has lapsed on an unresolved ticket. */}
                            {(ticket.slaFirstDueAt || ticket.slaResolveDueAt) && (
                                <ContextRow
                                    icon={<Schedule fontSize="small" color="action"/>}
                                    label="SLA"
                                    value={
                                        <Box>
                                            <Typography variant="body2" sx={{ color: firstOverdue ? 'error.main' : 'text.primary', fontWeight: firstOverdue ? 700 : 500 }}>
                                                First response: {fmtDue(ticket.slaFirstDueAt)}
                                                {ticket.firstResponseAt ? ' ✓' : firstOverdue ? ' — OVERDUE' : ''}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: resolveOverdue ? 'error.main' : 'text.primary', fontWeight: resolveOverdue ? 700 : 500 }}>
                                                Resolution: {fmtDue(ticket.slaResolveDueAt)}
                                                {ticket.resolvedAt ? ' ✓' : resolveOverdue ? ' — OVERDUE' : ''}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            )}

                            <Customer360Card ticketId={ticket.id} />
                            <ApprovalsPanel ticketId={ticket.id} />
                            {(ticket.status === 'resolved' || ticket.status === 'closed') && <QaReviewPanel ticketId={ticket.id} />}
                            <MvagPanel ticket={ticket} onChange={(patch) => setTicket((t) => ({ ...t!, ...patch }))} />

                            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px dashed #bdbdbd' }}>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>DESCRIPTION</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>"{ticket.description}"</Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Timeline (audit trail) | Notes (internal). Fills the rest of the column and
                        scrolls internally, so both this and the ticket card above stay independently usable. */}
                    <Card variant="outlined" sx={{ borderRadius: 2, flex: { md: 1 }, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <Tabs
                            value={sideTab}
                            onChange={(_, v) => setSideTab(v as 'timeline' | 'notes')}
                            variant="fullWidth"
                            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, flexShrink: 0 }}
                        >
                            <Tab value="timeline" label="Timeline" sx={{ minHeight: 40 }} />
                            <Tab value="notes" label={`Notes${notes.length ? ` (${notes.length})` : ''}`} sx={{ minHeight: 40 }} />
                        </Tabs>
                        <CardContent sx={{ pt: 1.5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {sideTab === 'timeline' ? (
                                events.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                        No events recorded yet.
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                                        {events.map((ev) => {
                                            const chip = eventChip(ev.type);
                                            return (
                                                <Box key={String(ev.id)} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        color={chip.color}
                                                        label={chip.label}
                                                        sx={{ minWidth: 84, justifyContent: 'center' }}
                                                    />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {eventSummary(ev)}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {relTime(ev.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0 }}>
                                    {/* Notes list scrolls (fills available height); the composer below is
                                        pinned (flexShrink:0) so "Add note" is always in view. */}
                                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, pr: 0.5 }}>
                                        {notes.length === 0 && (
                                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                                No internal notes yet.
                                            </Typography>
                                        )}
                                        {notes.map((n) => (
                                            <Box key={String(n.id)} sx={{ p: 1.5, bgcolor: '#fffde7', border: '1px solid #fff59d', borderRadius: 1.5 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{n.authorName}</Typography>
                                                    <Typography variant="caption" color="textSecondary">{relTime(n.createdAt)}</Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{n.body}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                    {/* Composer pinned below the scroll area — always reachable. */}
                                    {canWrite && (<>
                                    <TextField
                                        multiline
                                        minRows={2}
                                        size="small"
                                        placeholder="Add an internal note…"
                                        value={noteDraft}
                                        onChange={(e) => setNoteDraft(e.target.value)}
                                        inputProps={{ maxLength: 2000 }}
                                    />
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!noteDraft.trim() || noteSaving}
                                        onClick={handleAddNote}
                                        sx={{ alignSelf: 'flex-end' }}
                                    >
                                        Add note
                                    </Button>
                                    </>)}
                                    <Typography variant="caption" color="textSecondary">
                                        Internal — the customer never sees notes.
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                </Box>

                {/* RIGHT PANEL: Chat */}
                <Box sx={{ flex: { xs: 'none', md: 2 }, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                            <Typography variant="subtitle1" fontWeight="bold">Ticket Discussion</Typography>
                            <Typography variant="caption" color="textSecondary">Ticket ID: #{ticket.id} • Room ID: {ticket.chatRoomId}</Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#f0f2f5', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {!ticket.chatRoomId && (
                                <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }} data-testid="no-chat">
                                    <Typography variant="body2">This ticket has no chat{ticket.subCategory === 'safety_followup' ? ' — a next-day check-in after an SOS.' : ticket.source === 'sos' ? ' — it came from the SOS button.' : ticket.source === 'automation' ? ' — the system opened it (a follow-up or ride alert).' : ' (phone / manual ticket).'}</Typography>
                                    {ticket.source === 'sos' && can('sos:respond') && (
                                        <Button sx={{ mt: 1 }} variant="outlined" color="error" onClick={() => navigate('/safety-sos')} data-testid="open-sos-console">Open in SOS console</Button>
                                    )}
                                </Box>
                            )}
                            {!!ticket.chatRoomId && msgError && (
                                <Alert severity="warning" data-testid="msg-load-error" action={<Button size="small" color="inherit" onClick={() => loadData()}>Retry</Button>}>
                                    Couldn't load the messages.
                                </Alert>
                            )}
                            {messages.map((msg) => {
                                // Round 2 F7: staff on the right (you in dark blue, colleagues light blue), customer left, bot / system centred.
                                const kind = senderKind(msg, currentAdminUid);
                                if (kind === 'system') {
                                    return (
                                        <Box key={msg.id} sx={{ textAlign: 'center' }} data-testid="msg-system">
                                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>{msg.content}</Typography>
                                        </Box>
                                    );
                                }
                                const look = BUBBLE[kind];
                                const isMe = kind === 'me';
                                const label = senderLabel(msg, kind, {
                                    staffName: (sid) => agents.find((a) => String(a.id) === sid)?.name,
                                    customerRole: ticket.requesterType ?? ticket.requester?.role ?? null, // the side the ticket was raised as (dual-role accounts)
                                });

                                // Read receipts (own messages only): ✓ sent → ✓✓ delivered → ✓✓ (blue) seen.
                                // Persisted stamps come on the row; live upgrades via chat:read/chat:delivered.
                                const isSeen = isMe && (!!msg.readAt || (typeof msg.seq === 'number' && msg.seq <= readUpToSeq));
                                const isDelivered = isSeen || (isMe && (!!msg.deliveredAt || deliveredIds.has(String(msg.id))));

                                return (
                                    <Box key={msg.id} sx={{ display: 'flex', justifyContent: look.align, alignItems: 'flex-end', gap: 1 }} data-testid={`msg-${kind}`}>
                                        {kind === 'customer' && (
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: look.avatar }}><Person fontSize="small" /></Avatar>
                                        )}
                                        {kind === 'bot' && (
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: look.avatar }}><SmartToy fontSize="small" /></Avatar>
                                        )}

                                        <Paper elevation={1} sx={{ p: 1.5, maxWidth: '70%', bgcolor: look.bg, color: look.fg, borderRadius: look.radius, position: 'relative' }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.25, opacity: 0.85, color: isMe ? '#e3f2fd' : 'text.secondary' }} data-testid="msg-sender">{label}</Typography>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{msg.content}</Typography>
                                            <Typography variant="caption" sx={{
                                                display: 'block', mt: 0.5, opacity: 0.7, textAlign: 'right', fontSize: '0.7rem',
                                                color: isMe ? '#e3f2fd' : 'text.secondary'
                                            }}>
                                                {msg.editedAt && <Box component="span" sx={{ fontStyle: 'italic', mr: 0.5 }}>(edited)</Box>}
                                                {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                {isMe && (
                                                    <Box
                                                        component="span"
                                                        title={isSeen ? 'Seen' : isDelivered ? 'Delivered' : 'Sent'}
                                                        sx={{
                                                            ml: 0.5, fontWeight: 700, letterSpacing: '-1.5px',
                                                            color: isSeen ? '#40c4ff' : '#e3f2fd',
                                                            opacity: isSeen ? 1 : undefined,
                                                        }}
                                                    >
                                                        {isDelivered ? '✓✓' : '✓'}
                                                    </Box>
                                                )}
                                            </Typography>
                                        </Paper>

                                        {(kind === 'me' || kind === 'staff') && (
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: look.avatar }}><SupportAgent fontSize="small" /></Avatar>
                                        )}
                                    </Box>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </Box>

                        {/* Live presence: the customer is composing a reply right now. */}
                        {customerTyping && isChatActive && (
                            <Box sx={{ px: 2, pt: 0.75, bgcolor: '#fff' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        fontStyle: 'italic',
                                        animation: 'supportTypingPulse 1.4s ease-in-out infinite',
                                        '@keyframes supportTypingPulse': {
                                            '0%': { opacity: 0.35 },
                                            '50%': { opacity: 1 },
                                            '100%': { opacity: 0.35 },
                                        },
                                    }}
                                >
                                    Customer is typing…
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ p: 2, bgcolor: '#fff', borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
                            {isChatActive && canWrite && othersTicket ? (
                                <Box sx={{ width: '100%', p: 1, bgcolor: '#f5f5f5', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" color="textSecondary" data-testid="handled-by">
                                        Handled by <strong>{assigneeName}</strong> — only the owner replies.
                                    </Typography>
                                    <Button size="small" variant="outlined" disabled={claiming} onClick={handleAssignToMe}>Assign to me</Button>
                                </Box>
                            ) : isChatActive && canWrite ? (
                                <>
                                    {/* ⚡ canned replies — quick-insert into the reply box. */}
                                    <IconButton
                                        size="small"
                                        title="Canned replies"
                                        aria-label="Canned replies"
                                        onClick={(e) => setCannedAnchor(e.currentTarget)}
                                        sx={{ alignSelf: 'center' }}
                                    >
                                        <Bolt fontSize="small" />
                                    </IconButton>
                                    <Menu
                                        anchorEl={cannedAnchor}
                                        open={!!cannedAnchor}
                                        onClose={() => setCannedAnchor(null)}
                                    >
                                        {canned.length === 0 && (
                                            <MenuItem disabled>No canned replies configured</MenuItem>
                                        )}
                                        {canned.map((r) => (
                                            <MenuItem
                                                key={r.label}
                                                onClick={() => {
                                                    setNewMessage((prev) => (prev ? `${prev} ${r.text}` : r.text));
                                                    setCannedAnchor(null);
                                                }}
                                            >
                                                {r.label}
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                    <Button size="small" onClick={(e) => setMacroAnchor(e.currentTarget)} disabled={!macros.length} sx={{ alignSelf: 'center' }}>Macros</Button>
                                    <Menu anchorEl={macroAnchor} open={!!macroAnchor} onClose={() => setMacroAnchor(null)}>
                                        {macros.map((mc) => <MenuItem key={mc.id} onClick={() => runMacro(mc.id)}>{mc.label}</MenuItem>)}
                                    </Menu>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Type a reply..."
                                        size="small"
                                        value={newMessage}
                                        onChange={(e) => { setNewMessage(e.target.value); emitTyping(); }}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                    <Button
                                        variant="contained"
                                        endIcon={<SendIcon />}
                                        onClick={handleSendMessage}
                                        disabled={!newMessage.trim()}
                                        sx={{ borderRadius: 3, px: 3 }}
                                    >
                                        {isAssigned ? 'Send' : 'Claim & send'}
                                    </Button>
                                </>
                            ) : (
                                <Box sx={{ width: '100%', p: 1, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                        {isChatActive
                                            ? 'You can read this chat. Replying needs the support:write tick.'
                                            : <>This ticket is <strong>{STATUS_LABELS[ticket.status]}</strong>. Chat is disabled.</>}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Box>

            {/* FULL-WIDTH RIDE CARD — below the ticket/discussion split, spanning both columns. Shows the
                full ride context so an agent understands exactly what the ticket is about. */}
            {ticket.chatRoom?.ride && (() => {
                const r: any = ticket.chatRoom.ride;
                const money = (v: any) => (v == null ? '—' : `₹${Number(v).toFixed(2)}`);
                const dt = (v: any) => (v ? new Date(v).toLocaleString() : '—');
                const facts: Array<[string, any]> = [
                    ['Status', r.status ?? '—'],
                    ['Type', r.type ?? '—'],
                    ['Payment', `${r.paymentMethod ?? '—'}${r.paymentStatus ? ` (${r.paymentStatus})` : ''}`],
                    ['Requested', dt(r.requestedAt)],
                    ['Started', dt(r.startedAt)],
                    ['Completed', dt(r.completedAt)],
                    ['Distance', r.distanceMeters != null ? `${(r.distanceMeters / 1000).toFixed(2)} km` : '—'],
                    ['Duration', r.durationSeconds != null ? `${Math.round(r.durationSeconds / 60)} min` : '—'],
                    ['Base fare', money(r.baseFare)],
                    ['Distance fare', money(r.distanceFare)],
                    ['Waiting', money(r.waitingAmount)],
                    ['Tip', money(r.tip)],
                    ['Total fare', money(r.totalFare)],
                    ['Paid (cash)', money(r.paidByCash)],
                    ['Paid (wallet)', money(r.paidByWallet)],
                ];
                return (
                    <Card variant="outlined" sx={{ borderRadius: 2, borderTop: '4px solid #1976d2', mt: 2, flexShrink: 0 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DriveEta color="primary" /> Related Ride #{ticket.chatRoom.rideId}
                                </Typography>
                                <Button variant="outlined" size="small" onClick={() => navigate(`/ride/${ticket.chatRoom?.rideId}`)}>Open full ride page</Button>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 240 }}>
                                    <LocationOn fontSize="small" color="error" />
                                    <Typography variant="body2"><b>Pickup:</b> {r.pickupLocation}{r.pickupCity ? `, ${r.pickupCity}` : ''}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 240 }}>
                                    <LocationOn fontSize="small" color="success" />
                                    <Typography variant="body2"><b>Dropoff:</b> {r.dropoffLocation}{r.dropoffCity ? `, ${r.dropoffCity}` : ''}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5 }}>
                                {facts.map(([label, value]) => (
                                    <Box key={label}>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>{label}</Typography>
                                        <Typography variant="body2">{value}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            {r.cancelledAt && (
                                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                                    <Typography variant="body2"><b>Cancelled</b>{r.cancelledBy ? ` by ${r.cancelledBy}` : ''} — {r.cancellationReason || 'no reason given'}</Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                );
            })()}

            {/* Dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Status Update</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to change status to <strong>{STATUS_LABELS[pendingStatus]}</strong>?
                    </DialogContentText>
                    {needsCode && (
                        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                            <InputLabel id="resolution-code-label">Resolution</InputLabel>
                            <Select labelId="resolution-code-label" label="Resolution" value={resolutionCode} onChange={(e) => setResolutionCode(String(e.target.value))}>
                                {Object.entries(RESOLUTION_CODES).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                    {needsReason && (
                        <TextField fullWidth size="small" sx={{ mt: 2 }} label="Waiting on (reason)" placeholder="e.g. bank confirmation, payout provider"
                            value={holdReason} onChange={(e) => setHoldReason(e.target.value)} inputProps={{ maxLength: 200 }} />
                    )}
                    {statusError && <Alert severity="error" sx={{ mt: 2 }}>{statusError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmOpen(false); setPendingStatus(ticket.status); }} color="inherit">Cancel</Button>
                    <Button onClick={() => handleConfirmUpdate(ticket)} color="primary" variant="contained" disabled={!canConfirm} autoFocus>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Assign-to-agent picker */}
            <EscalateDialog
                ticketId={ticket.id}
                open={escalateOpen}
                onClose={() => setEscalateOpen(false)}
                onDone={(tier) => { setEscalateOpen(false); setTicket((t) => ({ ...t!, escalated: true, escalationTier: tier })); setBanner({ text: `Escalated to ${tier}.`, severity: 'warning' }); loadData(); }}
                onConflict={(message) => { setEscalateOpen(false); setBanner({ text: message, severity: 'warning' }); loadData(); }}
            />

            <TransferDialog
                ticketId={ticket.id}
                agents={agents}
                open={transferOpen}
                onClose={() => setTransferOpen(false)}
                onDone={() => { setTransferOpen(false); setBanner({ text: 'Ticket transferred.', severity: 'success' }); loadData(); }}
            />

            <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Assign ticket</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel>Agent</InputLabel>
                        <Select value={assignTarget} label="Agent" onChange={(e) => setAssignTarget(String(e.target.value))}>
                            {agents.map((a) => (
                                <MenuItem key={a.id} value={String(a.id)}>{a.name} — {a.role}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {agents.length === 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                            No active agents found (or you lack permission to list staff).
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleAssign} variant="contained" disabled={!assignTarget || claiming}>Assign</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                key={banner?.text}
                open={!!banner}
                autoHideDuration={bannerCritical ? 60000 : 10000}
                onClose={(_, reason) => {
                    if (reason === 'clickaway') return;               // clicking elsewhere never dismisses
                    if (reason !== 'timeout' && bannerLocked) return; // Esc/manual blocked for first 10s
                    setBanner(null);
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                {banner ? (
                    <Alert
                        onClose={bannerLocked ? undefined : () => setBanner(null)}
                        severity={banner.severity} variant="filled" sx={{ width: '100%' }}
                    >
                        {banner.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default SupportTicketDetail;